<template>
  <div class="tag-list">
    <a
      v-for="(item, idx) in list"
      :key="idx"
      class="tag-list__item"
      :href="getLink(item)"
      @click="e => handleClick(e, item)"
    >
      {{ item.tag_name }}
    </a>
    <span v-if="list.length > 15" class="tag-list__more">...</span>
  </div>
</template>

<script lang="ts" setup>
import type { NewTagNode, Template } from '~/api/interface';
import { PageKey } from '~/constants/page';
import { encodeTagUrlPath } from '~/utils/algorithm';
import { getTagIDs } from '..';

const props = defineProps<{
  new_tag_ids: Record<number, NewTagNode>;
  maxLength?: number;
  template?: Template;
}>();

// 递归展平标签节点树
function flattenNode(nodeMap: Record<number, NewTagNode>, fullMap: Record<number, NewTagNode>) {
  for (const key in nodeMap) {
    const node = nodeMap[key];
    fullMap[key] = node;
    const pNode = node.tag_pid ? fullMap[node.tag_pid] : null;
    node.tag_ids = `${node.level},${node.tag_id}`;
    if (pNode && node.level <= 2) {
      node.tag_ids = `${pNode.tag_ids}:${node.tag_ids}`;
    } else if (pNode) {
      node.tag_ids = pNode.tag_ids;
    }
    if (node.children) {
      flattenNode(node.children, fullMap);
    }
  }
}

// 过滤并展平标签列表
const list = computed(() => {
  const fullMap: Record<number, NewTagNode> = {};
  flattenNode(props.new_tag_ids, fullMap);
  return Object.values(fullMap)
    .filter(v => v.detail_is_show && v.level > 0)
    .slice(0, props.maxLength || 16);
});

const resourceLocation = injectResourceLocation();
const router = useRouter();

// 处理标签点击事件
const handleClick = (evt: Event, item: Partial<NewTagNode>) => {
  evt.preventDefault();

  const baseReportData = {
    tag_id: item.tag_id,
    tag_name: item.tag_name,
    source: resourceLocation?.source,
  };

  const reportData = props.template
    ? {
        ...baseReportData,
        template_id: props.template.id,
        template_title: props.template.name,
        tags: getTagIDs(props.template.tag_ids)
          .map(item => item.tag_name)
          .join(','),
      }
    : baseReportData;

  trackEvent(TRACK_EVENT.CLICK_POPUP_TEMPLATE_TAG, reportData);

  if (item.tag_ids) {
    const tagIds = item.tag_ids.split(':').slice(0, 2).join(':');
    router.push({
      name: PageKey.TEMPLATE,
      query: { tag_ids: tagIds },
    });
  }

  const open = useTemplateVisible();
  open.value = false;
};

// 生成标签链接
const getLink = (item: Partial<NewTagNode>) => {
  return item.tag_ids ? `/tags/${encodeTagUrlPath(item.tag_ids)}.html` : undefined;
};
</script>

<style scoped>
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 24px;
}

.tag-list__item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 4px 12px;
  font-size: 16px;
  color: var(--x-text-3);
  background-color: var(--x-back-3);
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;
}

.tag-list__item:hover {
  color: var(--x-text-1);
  background-color: var(--x-back-5);
}

.dark .tag-list__item {
  color: var(--x-text-1);
  background-color: var(--x-purple-1);
}

.dark .tag-list__item:hover {
  color: var(--x-text-1);
  background-color: var(--x-purple-1);
}

.tag-list__more {
  display: flex;
  align-items: center;
  color: var(--x-text-1);
}
</style>
